import {
  Chevron,
  Disclaimer,
  Div,
  H5,
  Icon,
  Pill,
  TooltipWrapper,
  TruncatedText,
  lightTheme,
} from '@vgrubs/components';

/**
 * The last column of a list row that opens a page: the arrow on the right,
 * as on the Verifications lists. Desktop only; on a phone the row is a card.
 * The header gets an empty column of the same width so the others line up.
 */
export const ARROW_COLUMN = { label: '', width: '2%' };

export const RowArrow = () => (
  <Div $desktop $width={ARROW_COLUMN.width} $flex $justifyEnd>
    <Icon $rotate="90deg">
      <Chevron />
    </Icon>
  </Div>
);

/**
 * The location name, cut to one line with the whole name in a tooltip, like
 * the store on the deliveries list. It always opens the location details in
 * a new tab, so the list stays where it was; the rest of the row is a link
 * to the location's receivables.
 *
 * Not an anchor itself (the row already is one, and links do not nest), so
 * the new tab is opened by hand, on a middle click too.
 */
export const LocationNameLink = ({
  locationId,
  name,
}: {
  locationId: number;
  name: string;
}) => {
  const openInNewTab = (event: React.MouseEvent) => {
    // Keeps the row's link from following the click as well.
    event.preventDefault();
    event.stopPropagation();
    window.open(`/locations/${locationId}`, '_blank', 'noopener');
  };

  return (
    <Div
      $minWidth="0"
      onClick={openInNewTab}
      onAuxClick={(event: React.MouseEvent) => {
        if (event.button === 1) openInNewTab(event);
      }}
    >
      <TooltipWrapper tooltip={name}>
        <H5 $m="0">
          <TruncatedText underline>{name}</TruncatedText>
        </H5>
      </TooltipWrapper>
    </Div>
  );
};

/**
 * A deleted (inactive) location - the list keeps the deleted ones that still
 * owe. Tinted like the Repayment plan tag; it sits under the name.
 */
export const InactiveTag = () => (
  // A row of its own: in the name's column it would stretch to full width.
  <Div $flex>
    <TooltipWrapper tooltip="This location was deleted">
      <Pill
        $background={lightTheme.transparentRed}
        $minWidth="0"
        $minWidthSM="0"
        $height="20px"
        $padding="0 8px"
        // Block-level flex, as on the Repayment plan tag: an inline pill in the
        // tooltip's link sits on a text baseline.
        style={{ flex: 'none', display: 'flex' }}
      >
        <Disclaimer $m="0" $bold $noWrap $color={lightTheme.primary}>
          Inactive
        </Disclaimer>
      </Pill>
    </TooltipWrapper>
  </Div>
);

/** The owner, cut the same way. */
export const OwnerName = ({ name }: { name: string }) => (
  // minWidth 0: next to the "Owner:" label on a phone card it is a flex
  // item, and without it a long name ran over whatever sat to its right
  // instead of ellipsizing.
  <TooltipWrapper tooltip={name} style={{ minWidth: 0 }}>
    <Disclaimer $m="0">
      <TruncatedText>{name}</TruncatedText>
    </Disclaimer>
  </TooltipWrapper>
);

/**
 * A status in a pill on desktop, like the delivery statuses; on phones it is
 * coloured text, like the status line on a delivery card. Grey stays the
 * normal text colour there, light grey reads as disabled on its own.
 */
export const StatusPill = ({
  color,
  children,
}: {
  color: string;
  children: React.ReactNode;
}) => (
  <>
    <Div $desktop>
      <StatusBadge color={color}>{children}</StatusBadge>
    </Div>
    <Div $mobile>
      <Disclaimer
        $m="0"
        $color={color === lightTheme.lightGray ? undefined : color}
      >
        {children}
      </Disclaimer>
    </Div>
  </>
);

/** Always a pill - for the top right of a phone card too. */
export const StatusBadge = ({
  color,
  children,
}: {
  color: string;
  children: React.ReactNode;
}) => (
  // One line: next to a long location name it shrinks, the name is the
  // part that gives way (it ellipsizes).
  <Pill $background={color} style={{ flex: 'none' }}>
    <Disclaimer
      $m="0"
      $color={lightTheme.white}
      $semiBold
      $lineHeight="1"
      style={{ whiteSpace: 'nowrap' }}
    >
      {children}
    </Disclaimer>
  </Pill>
);
